import { type ServiceParameter, type ServiceParameterGroup } from "../api";
import { Entity } from "../base/Entity";
import { type ResolvedParameterValue } from "../parameter-values";
import { ConfigurationParameterGroupEntity } from "./ConfigurationParameterGroupEntity";
import { ServiceParameterEntity } from "./ServiceParameterEntity";

/**
 * Represents a group of parameters in a service definition.
 * Parameter groups are used to organize related parameters together
 * and can be marked as required or optional.
 */
export class ServiceParameterGroupEntity
    extends Entity<ServiceParameterGroup>
    implements ServiceParameterGroup
{
    readonly name: string;
    readonly required: boolean;
    readonly parameters: ServiceParameterEntity[];
    readonly description?: string;
    readonly createdAt?: Date;
    readonly updatedAt?: Date;

    /**
     * Creates a new service configuration parameter group.
     */
    constructor(props: ServiceParameterGroup) {
        super(props.id);
        this.name = props.name;
        this.required = props.required ?? false;
        this.parameters = props.parameters.map(it => ServiceParameterEntity.create(it));
        this.description = props.description;
        this.createdAt = props.createdAt;
        this.updatedAt = props.updatedAt;
    }

    /**
     * Gets the display name of the parameter group.
     *
     * @returns The display name of the parameter group
     */
    getName(): string {
        return this.name;
    }

    /**
     * Gets whether this parameter group must have at least one parameter with a value.
     *
     * @returns Whether this parameter group must have at least one parameter with a value
     */
    isRequired(): boolean {
        return this.required;
    }

    /**
     * Gets the description of the parameter group.
     *
     * @returns The description of the parameter group
     */
    getDescription(): string | undefined {
        return this.description;
    }

    /**
     * Gets a parameter by its ID.
     *
     * @param id The ID of the parameter to get
     * @returns The parameter with the specified ID, or undefined if it doesn't exist
     */
    getParameter(id: ServiceParameter.Id): ServiceParameterEntity | undefined {
        return this.parameters.find(it => it.getId() === id);
    }

    /**
     * Gets the parameters in this group.
     *
     * @returns The parameters in this group
     */
    getParameters(): ServiceParameterEntity[] {
        return this.parameters;
    }

    /**
     * Applies a configuration to the parameter group. This method is used to
     * create a configuration parameter group entity from the parameter group
     * and the selected parameter values.
     *
     * @param parameterValues The parameter values to apply.
     * @param factorLookup The factor lookup to use.
     * @returns The applied configuration.
     */
    applyConfiguration(parameterValues: ResolvedParameterValue[]): ConfigurationParameterGroupEntity {
        return new ConfigurationParameterGroupEntity({
            id: this.id,
            name: this.name,
            description: this.description,
            parameters: this.parameters.map(it => it.applyConfiguration(parameterValues)),
        });
    }

    /**
     * Gets the values of all parameters in this group.
     *
     * @returns The values of all parameters in this group
     */
    getParameterValues(): ResolvedParameterValue[] {
        return this.parameters.map(parameter => parameter.getParameterValue());
    }

    /**
     * Creates a new service configuration parameter group with valid name.
     *
     * @param id The unique identifier for this parameter group
     * @param name The display name of the parameter group
     * @param required Whether this parameter group must have at least one parameter with a value
     * @param parameters Optional initial list of parameters in this group
     * @param description Optional description of the parameter group
     */
    static create(props: ServiceParameterGroup): ServiceParameterGroupEntity | never {
        if (!props.name || props.name.trim() === "") {
            throw new Error("ServiceParameterGroup: Property 'name' cannot be empty.");
        }

        return new ServiceParameterGroupEntity(props);
    }
}
